import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Checkbox,
  Divider,
  Drawer,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import { dashboardKeys } from "../../../api/queryKeys";
import ContainedButton from "../../../components/buttons/ContainedButton";
import CustomTooltip from "../../../components/CustomTooltip";
import SearchInput from "../../../components/filters/SearchInput";
import LoadingIcon from "../../../components/LoadingIcon";
import { Toast } from "../../../components/Toast";
import { EnvFeatureFlagContext } from "../../../contexts/EnvFeaturesContext";
import SvgToIcon from "../../../icons/SvgToIcon";
import { chartIcons, icons } from "../../stack/environment/icons/iconExport";
import { useDashboard } from "../hooks/useDashboard";
import CustomWidgetWizard from "./CustomWidgetWizard";
import SettingsButton from "./SettingsButton";
import { getAvailableWidgets } from "./widgetDefinitions";

const WidgetSelector = ({ widgets }) => {
  const colors = useColors();
  const { trackEvent, getAllCustomWidgets, createCustomWidget } = useAPI();
  const [appliedFilters, setAppliedFilters] = useState({
    searchString: "",
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedWidgets, setSelectedWidgets] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [wizardOpen, setWizardOpen] = useState(false);
  const { postureTrendChart, isActiveRds } = useContext(EnvFeatureFlagContext);

  const { saveDashboardWithWidgets, isSaving, isSaved } = useDashboard();

  const { data: customWidgetsResponse } = useQuery({
    queryKey: dashboardKeys.customWidgets(),
    queryFn: getAllCustomWidgets,
    select: (response) => response?.data || [],
  });

  const customWidgets = customWidgetsResponse || [];

  const createWidgetMutation = useMutation({
    mutationFn: createCustomWidget,
    onSuccess: () => {
      Toast.info(
        "Your custom widget is being created. This may take a few minutes to complete."
      );
    },
    onError: (error) => {
      Toast.error(
        error?.response?.data?.detail || "Failed to create custom widget"
      );
    },
  });

  // Initialize selected widgets when drawer opens or widgets change
  useEffect(() => {
    setSelectedWidgets(widgets.map((w) => w.id));
  }, [widgets]);

  // Handle checkbox toggle
  const handleWidgetToggle = (widgetId, isChecked) => {
    if (isChecked) {
      trackEvent("User checked widget", { widgetId });
      setSelectedWidgets((prev) => [...prev, widgetId]);
    } else {
      trackEvent("User unchecked widget", { widgetId });
      setSelectedWidgets((prev) => prev.filter((id) => id !== widgetId));
    }
  };

  // Handle submit - save dashboard with selected widgets
  const handleSubmit = () => {
    trackEvent("User submitted widget selector");
    const availableWidgets = getAvailableWidgets(postureTrendChart);
    const standardWidgets = availableWidgets.filter((widget) =>
      selectedWidgets.includes(widget.id)
    );

    const selectedCustomWidgetIds = selectedWidgets.filter((id) =>
      id.startsWith("custom_")
    );
    const customWidgetsToSave = customWidgets
      .filter((widget) =>
        selectedCustomWidgetIds.includes(`custom_${widget.custom_widget_id}`)
      )
      .map((customWidget) => ({
        id: `custom_${customWidget.custom_widget_id}`,
        title: customWidget.widget_title,
        description: customWidget?.description || "",
        contentType: "component",
        componentType: "CustomWidget",
        chartType: customWidget.widget_type,
        w: 6,
        h: 4,
        minH: 3,
        minW: 6,
        static: false,
      }));

    const newWidgets = [...standardWidgets, ...customWidgetsToSave];
    saveDashboardWithWidgets(newWidgets);
  };
  useEffect(() => {
    if (isSaved) {
      setDrawerOpen(false);
    }
  }, [isSaved]);

  // Check if there are unsaved changes
  const hasChanges = () => {
    const currentWidgetIds = widgets.map((w) => w.id).sort();
    const selectedWidgetIds = [...selectedWidgets].sort();
    return (
      JSON.stringify(currentWidgetIds) !== JSON.stringify(selectedWidgetIds)
    );
  };

  // Map widget chart types to icons
  const getWidgetIcon = (widget) => {
    const chartType = widget.chartType;

    switch (chartType) {
      case "table":
        return chartIcons.TableChartIcon;
      case "heatmap":
        return chartIcons.HeatmapChartIcon;
      case "bar":
        return chartIcons.BarChartIcon;
      case "pie":
        return chartIcons.PieChartIcon;
      case "donut":
        return chartIcons.DonutChartIcon;
      case "gauge":
        return chartIcons.GaugeChartIcon;
      case "stacked-bar":
        return chartIcons.StackedBarChartIcon;
      case "metrics":
        return chartIcons.MetricsChartIcon;
      case "line":
        return chartIcons.LineChartIcon;
      case "two-line":
        return chartIcons.TwoLineChartIcon;
      default:
        // Default based on component type or use a general chart icon
        if (widget.componentType === "OperationalHeatmap") {
          return chartIcons.HeatmapChartIcon;
        }
        return chartIcons.TableChartIcon;
    }
  };

  const availableWidgets = getAvailableWidgets(postureTrendChart);
  const filteredWidgets = availableWidgets.filter(
    (widget) =>
      widget.title
        .toLowerCase()
        .includes(appliedFilters.searchString.toLowerCase()) ||
      (widget.description &&
        widget.description
          .toLowerCase()
          .includes(appliedFilters.searchString.toLowerCase()))
  );

  const filteredCustomWidgets = customWidgets.filter((widget) =>
    widget.widget_title
      ?.toLowerCase()
      .includes(appliedFilters.searchString.toLowerCase())
  );

  const handleCreateWidget = async (widgetData) => {
    await createWidgetMutation.mutateAsync(widgetData);
  };

  const getCustomWidgetIcon = (widgetType) => {
    switch (widgetType) {
      case "bar":
        return chartIcons.BarChartIcon;
      case "pie":
        return chartIcons.PieChartIcon;
      default:
        return chartIcons.TableChartIcon;
    }
  };

  return (
    <>
      <CustomTooltip title="Customize dashboard view" placement="top">
        <Box>
          <SettingsButton setDrawerOpen={setDrawerOpen} />
        </Box>
      </CustomTooltip>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => {
          trackEvent("User closed widget selector");
          setDrawerOpen(false);
          setSelectedWidgets(widgets.map((w) => w.id));
        }}
        PaperProps={{
          sx: {
            width: 415,
            backgroundColor: colors.grey_50,
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            height: "100%",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              height: "calc(100% - 100px)",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="subtitle1">Customize View</Typography>
                <IconButton
                  onClick={() => setDrawerOpen(false)}
                  sx={{ color: colors.text_secondary }}
                >
                  <CloseIcon
                    sx={{
                      color: colors.icon_action_active,
                      width: "20px",
                      height: "20px",
                    }}
                  />
                </IconButton>
              </Box>

              <Typography variant="body1">
                Select widgets to view in your dashboard
              </Typography>
            </Box>

            <Tabs
              value={activeTab}
              onChange={(event, newValue) => setActiveTab(newValue)}
              sx={{
                borderBottom: `1px solid ${colors.grey_20percent}`,
                "& .MuiTab-root": {
                  textTransform: "none",
                  minHeight: "40px",
                },
              }}
            >
              <Tab label="Standard Widgets" />
              {isActiveRds && <Tab label="Custom Widgets" />}
            </Tabs>

            <SearchInput
              fieldName="searchString"
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
            />

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                overflowY: "auto",
              }}
            >
              {activeTab === 0 &&
                filteredWidgets.map((widget) => {
                  const isChecked = selectedWidgets.includes(widget.id);
                  const WidgetIcon = getWidgetIcon(widget);

                  return (
                    <Box
                      key={widget.id}
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Box
                        onClick={() =>
                          handleWidgetToggle(widget.id, !isChecked)
                        }
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          borderRadius: "8px",
                          gap: 2,
                          p: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Checkbox
                            checked={isChecked}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleWidgetToggle(widget.id, e.target.checked);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            icon={
                              <SvgToIcon
                                IconLogo={icons.Unchecked}
                                style={{ width: "20px", height: "20px" }}
                              />
                            }
                            checkedIcon={
                              <SvgToIcon
                                IconLogo={icons.Checked}
                                style={{ width: "20px", height: "20px" }}
                              />
                            }
                            sx={{
                              padding: "2px",
                            }}
                          />
                          <Box>
                            <Typography
                              variant="subtitle2"
                              sx={{ color: colors.text_primary }}
                            >
                              {widget.title}
                            </Typography>
                            {widget.description && (
                              <Typography
                                variant="body2"
                                sx={{ color: colors.text_secondary }}
                              >
                                {widget.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <SvgToIcon
                          IconLogo={WidgetIcon}
                          style={{
                            width: "48px",
                            height: "48px",
                            color: colors.icon_action_active,
                          }}
                        />
                      </Box>
                      <Divider />
                    </Box>
                  );
                })}

              {activeTab === 1 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                      pb: 1,
                      backgroundColor: colors.grey_50,
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ color: colors.text_primary }}
                    >
                      Custom Widgets
                    </Typography>
                    <CustomTooltip title="Create custom widget" placement="top">
                      <IconButton onClick={() => setWizardOpen(true)}>
                        <SvgToIcon IconLogo={icons.AddCircleIcon} />
                      </IconButton>
                    </CustomTooltip>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      overflowY: "auto",
                      flex: 1,
                    }}
                  >
                    {filteredCustomWidgets.length === 0 ? (
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.text_secondary,
                          textAlign: "center",
                          py: 4,
                        }}
                      >
                        No custom widgets found. Click the + button to create
                        one.
                      </Typography>
                    ) : (
                      filteredCustomWidgets.map((widget) => {
                        const widgetId = `custom_${widget.custom_widget_id}`;
                        const isChecked = selectedWidgets.includes(widgetId);
                        const WidgetIcon = getCustomWidgetIcon(
                          widget.widget_type
                        );

                        return (
                          <Box
                            key={widget.custom_widget_id}
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            <Box
                              onClick={() =>
                                handleWidgetToggle(widgetId, !isChecked)
                              }
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "space-between",
                                cursor: "pointer",
                                borderRadius: "8px",
                                gap: 2,
                                p: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Checkbox
                                  checked={isChecked}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleWidgetToggle(
                                      widgetId,
                                      e.target.checked
                                    );
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  icon={
                                    <SvgToIcon
                                      IconLogo={icons.Unchecked}
                                      style={{ width: "20px", height: "20px" }}
                                    />
                                  }
                                  checkedIcon={
                                    <SvgToIcon
                                      IconLogo={icons.Checked}
                                      style={{ width: "20px", height: "20px" }}
                                    />
                                  }
                                  sx={{
                                    padding: "2px",
                                  }}
                                />
                                <Box>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ color: colors.text_primary }}
                                  >
                                    {widget.widget_title}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: colors.text_secondary }}
                                  >
                                    {widget.description ||
                                      `${widget.widget_type} chart`}
                                  </Typography>
                                </Box>
                              </Box>
                              <SvgToIcon
                                IconLogo={WidgetIcon}
                                style={{
                                  width: "48px",
                                  height: "48px",
                                  color: colors.icon_action_active,
                                }}
                              />
                            </Box>
                            <Divider />
                          </Box>
                        );
                      })
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
          {/* Submit Button */}
          <Box
            sx={{
              mt: 2,
              display: "flex",
              gap: 2,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ContainedButton
              onClick={handleSubmit}
              color="primary"
              size="small"
              loading={isSaving}
              disabled={!hasChanges()}
            >
              {isSaving ? <LoadingIcon size={12} /> : "Apply Changes"}
            </ContainedButton>
          </Box>
        </Box>
      </Drawer>
      <CustomWidgetWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onSubmit={handleCreateWidget}
        isLoading={createWidgetMutation.isPending}
      />
    </>
  );
};

export default WidgetSelector;
